import {Participant, Role, ServiceCenter, Stage} from "../../../../../types/domain";
import {Alert, Button, Input, Modal, NumberInput, Select, TextInput} from "@mantine/core";
import React, {useEffect, useState} from "react";
import ServicecenterService from "../../../../../services/servicecenter.service";
import ParticipantService from "../../../../../services/participant.service";
import StageService from "../../../../../services/stage.service";
import {DateInput, DateValue} from "@mantine/dates";
import {convertDateFormat} from "../helperFunctions";
import * as Sentry from "@sentry/react";

type ParticipantModalProps = {
    opened: boolean,
    close: () => void,
    title?: string,
    participant: Participant | null,
    userRole: Role | null,
    userServiceCenters: Array<ServiceCenter>
};

export function ParticipantModal({
                                     opened,
                                     close,
                                     title,
                                     participant,
                                     userRole,
                                     userServiceCenters
                                 }: ParticipantModalProps) {
    const [firstname, setFirstname] = useState("");
    const [lastname, setLastname] = useState("");
    const [birthDate, setBirthDate] = useState<string | null>(null);
    const [stage, setStage] = useState("");
    const [serviceCenter, setServiceCenter] = useState("");
    const [serviceCenters, setServiceCenters] = useState<ServiceCenter[]>([]);
    const [stages, setStages] = useState<Stage[]>([]);

    const [showInputAlert, setShowInputAlert] = useState(false);
    const [showAlert, setShowAlert] = useState(false);

    //Synchronizing
    useEffect(() => {
        /**
         * Loads all servicecenters from DB
         */
        async function loadServicecenters() {
            try {
                if (userRole?.name === 'Admin') {
                    const response = await ServicecenterService.getAll();
                    setServiceCenters(response.data);
                } else if (userRole?.name === 'DSS Leiter*in') {
                    setServiceCenters(userServiceCenters);
                }
            } catch (e) {
                console.error("Error while reading servicecenters: ", e);
            }
        }

        /**
         * Loads all stages from DB
         */
        async function loadStages() {
            try {
                const response = await StageService.getAll();
                setStages(response.data);
            } catch (e) {
                console.error("Error while reading stages: ", e);
            }
        }

        loadServicecenters();
        loadStages();

        if (participant) {
            setFirstname(participant.firstname ?? "");
            setLastname(participant.lastname ?? "");
            setBirthDate(participant.birthDate);
            setStage(participant.stage?.name ?? "Prophylaxe");

            // Check where to get the servicecenter from
            const scFromGroup = participant.group?.serviceCenter;
            if (scFromGroup) {
                setServiceCenter(scFromGroup.id.toString());
            } else {
                setServiceCenter(participant.serviceCenter?.id.toString() ?? "");
            }
        } else {
            // New participant
            setFirstname("");
            setLastname("");
            setBirthDate(null);
            setStage("Prophylaxe");
            setServiceCenter("");
        }

        setShowInputAlert(false);
        setShowAlert(false);
    }, [participant, opened]);

    /**
     * Change event of birthDate value
     * @param newValue new date value
     */
    function dateChange(newValue: DateValue) {
        if (newValue) {
            const formattedDate = convertDateFormat(newValue);
            setBirthDate(formattedDate);
        } else {
            setBirthDate(null);
        }
    }

    /**
     * updates a participant in the DB
     */
    async function updateParticipant() {
        if (!lastname.trim() || !firstname.trim() || !serviceCenter.trim()) {
            setShowInputAlert(true);
            return;
        }

        setShowInputAlert(false);
        if (participant) {
            participant.firstname = firstname;
            participant.lastname = lastname;
            participant.birthDate = birthDate;
            participant.stage = stages.find(s => s.name === stage);

            const scFromGroup = participant.group?.serviceCenter;
            if (scFromGroup) {
                participant.serviceCenter = null;
            } else {
                participant.serviceCenter = serviceCenters.find(sc => sc.id.toString() === serviceCenter);

            }
            await ParticipantService.update(participant, participant.id)
                .catch((e: Error) => {
                    Sentry.captureException(e, {
                        extra: {
                            request: {
                                participant: JSON.parse(JSON.stringify(participant)),
                            },
                        },
                    });
                    setShowAlert(true);
                    close();
                    return;
                });

            close();
        } else {
            setShowAlert(true);
        }
    }

    /**
     * Adds a new participant to the DB
     */
    async function addParticipant() {
        if (!lastname.trim() || !firstname.trim() || !serviceCenter.trim()) {
            setShowInputAlert(true);
            return;
        }

        setShowInputAlert(false);

        const p: Participant = {
            id: 0,
            firstname: firstname,
            lastname: lastname,
            birthDate: birthDate,
            serviceCenter: serviceCenters.find(sc => sc.id.toString() === serviceCenter),
            stage: stages.find(s => s.name === stage),
            active: true,
            group: null
        };

        await ParticipantService.create(p)
            .catch((e: Error) => {
                Sentry.captureException(e, {
                    extra: {
                        request: {
                            participant: JSON.parse(JSON.stringify(p)),
                        },
                    },
                });
                setShowAlert(true);
                close();
            });

        close();
    }

    return (
        <Modal opened={opened} onClose={close} centered title={title}>
            <Input.Wrapper>
                <TextInput
                    label="Vorname"
                    mb="sm"
                    value={firstname}
                    withAsterisk
                    onChange={(e) => setFirstname(e.currentTarget.value)}
                />
                <TextInput
                    label="Nachname"
                    mb="sm"
                    value={lastname}
                    withAsterisk
                    onChange={(e) => setLastname(e.currentTarget.value)}
                />

                <DateInput
                    id="birthDate"
                    value={birthDate ? new Date(birthDate) : null}
                    onChange={dateChange}
                    valueFormat="DD.MM.YYYY"
                    maxDate={new Date()}
                    label="Geburtsdatum"
                    locale="de"
                    clearable
                />

                <NumberInput
                    label="Stadium"
                    mb="sm"
                    value={/^\d+$/.test(stage) ? parseInt(stage, 10) : 0}
                    description="0 = Prophylaxe"
                    min={0}
                    max={7}
                    step={1}
                    withAsterisk
                    onChange={(value) => {
                        if (value === 0) {
                            setStage("Prophylaxe");
                        } else {
                            setStage(value.toString());
                        }
                    }}
                />

                <Select
                    label="Servicestelle"
                    mb="sm"
                    withinPortal
                    data={serviceCenters.map(sc => ({
                        value: sc.id.toString(),
                        label: sc.name
                    }))}
                    value={serviceCenter}
                    disabled={participant?.group?.serviceCenter !== undefined} //disable for group members
                    description={participant?.group?.serviceCenter !== undefined ? "Ist Gruppenmitglied" : ""}
                    withAsterisk
                    onChange={(value) => {
                        if (value) {
                            setServiceCenter(value);
                        }
                    }}
                />
            </Input.Wrapper>
            {
                showInputAlert ?
                    <Alert w={"100%"}
                           title={participant ? "Aktualisieren fehlgeschlagen" : "Hinzufügen fehlgeschlagen"}
                           ta={"left"} color="red" mt={"10px"}>
                        Bitte füllen Sie alle Felder aus!
                    </Alert> : <></>
            }
            {
                showAlert ?
                    <Alert w={"100%"}
                           title={participant ? "Aktualisieren fehlgeschlagen" : "Hinzufügen fehlgeschlagen"}
                           ta={"left"} color="red" mt={"10px"}>
                        Leider ist ein Fehler während des {participant ? "aktualisieren" : "einfügen"} aufgetreten.
                        Versuchen Sie es noch einmal oder kontaktieren Sie die Administratoren.
                    </Alert> : <></>
            }
            <div className="buttonRow">
                <Button onClick={participant ? updateParticipant : addParticipant} mb="sm">
                    {participant ? "Aktualisieren" : "Hinzufügen"}
                </Button>
            </div>
        </Modal>
    );
}
